import type { HealthLevel } from "@cva/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet } from "@tanstack/react-router";
import {
  ArrowLeft,
  BarChart3,
  Bell,
  Building2,
  DatabaseBackup,
  FileText,
  FileX,
  Gauge,
  History,
  Layers,
  Lock,
  ShieldAlert,
  ShieldCheck,
  Users,
} from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { EnvBadge, Logo } from "../../components/env-badge";
import { LegalLinks } from "../../components/legal-links";
import { Button } from "../../components/ui/button";
import { Badge, Skeleton } from "../../components/ui/card";
import { Field, Input } from "../../components/ui/input";
import { client, orpc } from "../../lib/api";
import { lastOrg } from "../../lib/session";
import { cn, errorMessage } from "../../lib/utils";

/**
 * Admin area (docs/13-betrieb.md). The server decides everything; this layout only
 * shows the right step: not found → set up 2FA → enter code → admin pages.
 */

export const adminSessionQuery = () => ({
  ...orpc.admin.session.queryOptions(),
  staleTime: 30_000,
  refetchInterval: 60_000,
});

const LOCK_REASONS = new Set(["admin-unlock", "admin-2fa-setup"]);

/** When the unlock expires while a page is open, go back to the code prompt. */
function useLockWatcher() {
  const queryClient = useQueryClient();
  useEffect(
    () =>
      queryClient.getQueryCache().subscribe((event) => {
        const error = event.query.state.error as { data?: { reason?: string } } | null;
        if (
          event.type === "updated" &&
          error?.data?.reason &&
          LOCK_REASONS.has(error.data.reason)
        ) {
          void queryClient.invalidateQueries({ queryKey: orpc.admin.session.key() });
        }
      }),
    [queryClient],
  );
}

export const LEVEL_STYLE: Record<
  HealthLevel,
  { dot: string; label: string; tone: "success" | "warning" | "danger" | "neutral" }
> = {
  ok: { dot: "bg-emerald-500", label: "OK", tone: "success" },
  warning: { dot: "bg-amber-500", label: "Warnung", tone: "warning" },
  critical: { dot: "bg-red-600", label: "Kritisch", tone: "danger" },
  unknown: { dot: "bg-zinc-300", label: "Unbekannt", tone: "neutral" },
};

export function StatusDot({ level, className }: { level: HealthLevel; className?: string }) {
  return (
    <span
      role="img"
      aria-label={LEVEL_STYLE[level].label}
      className={cn(
        "inline-block size-2.5 shrink-0 rounded-full",
        LEVEL_STYLE[level].dot,
        className,
      )}
    />
  );
}

export function AdminPageHeader({
  title,
  description,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-[28px] leading-[34px] font-bold tracking-tight text-ink">{title}</h1>
        {description ? <p className="mt-1 text-sm text-zinc-500">{description}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-full flex-col items-center justify-center bg-canvas px-5 py-12">
      <div className="mb-8 flex items-center gap-3">
        <Logo size="lg" />
        <EnvBadge />
      </div>
      <div className="w-full max-w-sm rounded-xl border border-zinc-200 bg-white p-7 shadow-sm">
        {children}
      </div>
      <LegalLinks className="mt-8" />
    </div>
  );
}

function UnlockForm() {
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const session = await client.admin.unlock({ code, method: backup ? "backup" : "totp" });
      queryClient.setQueryData(orpc.admin.session.queryKey(), session);
      await queryClient.invalidateQueries({
        predicate: (q) => JSON.stringify(q.queryKey).includes("admin"),
      });
    } catch (caught) {
      setError(errorMessage(caught, "Der Code ist falsch."));
      setCode("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Centered>
      <div className="mb-5 flex size-11 items-center justify-center rounded-full bg-zinc-900 text-white">
        <Lock className="size-5" />
      </div>
      <h1 className="text-xl font-semibold text-zinc-900">Administration</h1>
      <p className="mt-1 mb-5 text-sm text-zinc-500">
        {backup
          ? "Gib einen Wiederherstellungscode ein. Er ist danach verbraucht."
          : "Bestätige den Zugang mit dem Code aus deiner Authenticator-App."}
      </p>
      <form onSubmit={submit} className="space-y-4">
        <Field label={backup ? "Wiederherstellungscode" : "Code"} error={error}>
          <Input
            autoFocus
            required
            inputMode={backup ? "text" : "numeric"}
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={backup ? "xxxxx-xxxxx" : "123456"}
          />
        </Field>
        <Button type="submit" className="w-full" loading={busy}>
          Entsperren
        </Button>
      </form>
      <div className="mt-6 flex justify-between text-sm">
        <a href="/" className="text-zinc-500 hover:text-zinc-900">
          Zur App
        </a>
        <button
          type="button"
          className="font-medium text-brand-600 hover:text-brand-700"
          onClick={() => setBackup(!backup)}
        >
          {backup ? "Code aus der App" : "Wiederherstellungscode"}
        </button>
      </div>
    </Centered>
  );
}

function NavItem({
  to,
  icon,
  children,
  exact,
}: {
  to: string;
  icon: ReactNode;
  children: ReactNode;
  exact?: boolean;
}) {
  return (
    <Link
      to={to}
      activeOptions={{ exact }}
      className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium text-zinc-400 hover:bg-white/5 hover:text-white data-[status=active]:bg-white/10 data-[status=active]:text-white [&_svg]:size-4"
    >
      {icon}
      <span className="flex-1">{children}</span>
    </Link>
  );
}

export function AdminLayout() {
  useLockWatcher();
  const queryClient = useQueryClient();
  const session = useQuery(adminSessionQuery());
  const back = lastOrg();

  if (session.isLoading) {
    return (
      <div className="p-10">
        <Skeleton className="h-64" />
      </div>
    );
  }
  const data = session.data;
  if (!data?.isAdmin) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
        <p className="text-4xl font-semibold text-zinc-300">404</p>
        <p className="text-zinc-600">Diese Seite gibt es nicht.</p>
        <a href="/" className="text-sm font-medium text-brand-600">
          Zur Startseite
        </a>
      </div>
    );
  }
  if (data.requires2fa && !data.twoFactorEnabled) {
    return (
      <Centered>
        <div className="mb-5 flex size-11 items-center justify-center rounded-full bg-amber-100 text-amber-700">
          <ShieldAlert className="size-5" />
        </div>
        <h1 className="text-xl font-semibold text-zinc-900">Zwei-Faktor-Anmeldung nötig</h1>
        <p className="mt-2 text-sm text-zinc-600">
          Der Admin-Bereich ist nur mit Zwei-Faktor-Anmeldung zugänglich. Richte sie unter „Konto“
          mit einer Authenticator-App ein und komm dann hierher zurück.
        </p>
        <Button asChild className="mt-6 w-full">
          <a href="/account">Zum Konto</a>
        </Button>
      </Centered>
    );
  }
  if (!data.unlocked) return <UnlockForm />;

  const alerts = data.openAlerts;
  const alertCount = (alerts?.warning ?? 0) + (alerts?.critical ?? 0);

  return (
    <div className="flex min-h-full">
      <aside className="sticky top-0 flex h-screen w-60 shrink-0 flex-col bg-zinc-950 text-white">
        <div className="flex items-center gap-2 px-4 pt-4 pb-1">
          <ShieldCheck className="size-5 text-emerald-400" />
          <span className="font-semibold tracking-tight">Administration</span>
        </div>
        <div className="px-4 pb-3">
          <EnvBadge />
        </div>
        <nav className="flex-1 space-y-0.5 px-2">
          <NavItem to="/admin" icon={<Gauge />} exact>
            Übersicht
          </NavItem>
          <NavItem to="/admin/traffic" icon={<BarChart3 />}>
            Besucher
          </NavItem>
          <NavItem to="/admin/users" icon={<Users />}>
            Nutzer:innen
          </NavItem>
          <NavItem to="/admin/orgs" icon={<Building2 />}>
            Organisationen
          </NavItem>
          <NavItem to="/admin/jobs" icon={<Layers />}>
            Jobs
          </NavItem>
          <NavItem to="/admin/alerts" icon={<Bell />}>
            Warnungen
            {alertCount > 0 ? (
              <span
                className={cn(
                  "ml-2 rounded-full px-1.5 py-0.5 text-[11px] font-semibold",
                  alerts?.critical ? "bg-red-600 text-white" : "bg-amber-400 text-zinc-950",
                )}
              >
                {alertCount}
              </span>
            ) : null}
          </NavItem>
          <NavItem to="/admin/cancellations" icon={<FileX />}>
            Kündigungen
          </NavItem>
          <NavItem to="/admin/backups" icon={<DatabaseBackup />}>
            Backups
          </NavItem>
          <NavItem to="/admin/audit" icon={<History />}>
            Audit-Log
          </NavItem>
          <NavItem to="/admin/legal" icon={<FileText />}>
            Rechtliches
          </NavItem>
        </nav>
        <div className="space-y-2 border-t border-white/10 p-3 text-xs text-zinc-400">
          {data.unlockedUntil ? (
            <p>
              Entsperrt bis{" "}
              {data.unlockedUntil.toLocaleTimeString("de-DE", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          ) : null}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-zinc-300 hover:bg-white/10 hover:text-white"
              onClick={async () => {
                await client.admin.lock();
                await queryClient.invalidateQueries({ queryKey: orpc.admin.session.key() });
              }}
            >
              <Lock /> Sperren
            </Button>
            <a
              href={back ? `/o/${back}` : "/"}
              className="inline-flex h-7 items-center gap-1.5 rounded-lg px-2 text-zinc-300 hover:bg-white/10 hover:text-white"
            >
              <ArrowLeft className="size-3.5" /> Zur App
            </a>
          </div>
        </div>
      </aside>
      <main className="min-w-0 flex-1 bg-zinc-50">
        <div className="mx-auto max-w-6xl px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export function RoleBadges({
  user,
}: {
  user: { role: string; banned: boolean; twoFactorEnabled: boolean; emailVerified: boolean };
}) {
  return (
    <span className="flex flex-wrap gap-1">
      {user.role === "admin" ? <Badge tone="brand">Admin</Badge> : null}
      {user.banned ? <Badge tone="danger">Gesperrt</Badge> : null}
      {user.twoFactorEnabled ? <Badge tone="success">2FA</Badge> : null}
      {!user.emailVerified ? <Badge>unbestätigt</Badge> : null}
    </span>
  );
}
